"use client";

import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { Menu, X } from "lucide-react";
import { useSession } from "next-auth/react";
import { categoryPagePath } from "@/lib/page-type";

interface MobileMenuProps {
    links: { label: string; href: string }[];
    categories: any[];
}

export default function MobileMenu({ links, categories }: MobileMenuProps) {
    const { data: session } = useSession();
    const user = session?.user;
    const [isOpen, setIsOpen] = useState(false);
    const openerRef = useRef<HTMLButtonElement>(null);
    const dialogRef = useRef<HTMLDivElement>(null);

    // Prevent scrolling when menu is open (batched to avoid forced reflow)
    useEffect(() => {
        requestAnimationFrame(() => {
            if (isOpen) {
                document.body.style.overflow = "hidden";
            } else {
                document.body.style.removeProperty("overflow");
            }
        });
        return () => {
            requestAnimationFrame(() => {
                document.body.style.removeProperty("overflow");
            });
        };
    }, [isOpen]);

    // Escape closes the menu, Tab stays inside it, focus moves in on open and returns to the opener on close.
    useEffect(() => {
        if (!isOpen) return;
        const opener = openerRef.current;
        const dialog = dialogRef.current;
        dialog?.querySelector<HTMLElement>("button, a[href]")?.focus();
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                setIsOpen(false);
                return;
            }
            if (e.key !== "Tab" || !dialog) return;
            const items = Array.from(dialog.querySelectorAll<HTMLElement>("button, a[href]"));
            if (items.length === 0) return;
            const first = items[0];
            const last = items[items.length - 1];
            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        };
        window.addEventListener("keydown", onKeyDown);
        return () => {
            window.removeEventListener("keydown", onKeyDown);
            opener?.focus();
        };
    }, [isOpen]);

    return (
        <div className="md:hidden">
            <button
                ref={openerRef}
                type="button"
                onClick={() => setIsOpen(true)}
                className="text-white p-3 hover:text-cs-accent transition-colors"
                aria-label="Open Menu"
                aria-haspopup="dialog"
                aria-expanded={isOpen}
            >
                <Menu className="w-6 h-6" />
            </button>

            {/* Overlay */}
            {isOpen && (
                <div ref={dialogRef} role="dialog" aria-modal="true" aria-label="Menu" className="fixed inset-0 z-[100] bg-black/98 backdrop-blur-xl flex flex-col p-6 animate-in fade-in slide-in-from-right-10 duration-200 overflow-y-auto">
                    <div className="flex justify-between items-center mb-12">
                        <span className="text-xl font-bold text-white">Menu</span>
                        <button
                            type="button"
                            onClick={() => setIsOpen(false)}
                            className="text-gray-400 hover:text-white p-3 bg-white/5 rounded-full"
                            aria-label="Close Menu"
                        >
                            <X className="w-6 h-6" />
                        </button>
                    </div>

                    <nav aria-label="Mobile" className="flex flex-col gap-6 text-center">
                        {/* Top Coupons (First) */}
                        <Link
                            href="/top-coupons"
                            onClick={() => setIsOpen(false)}
                            className="text-2xl font-bold text-white hover:text-[#00ff9d] transition-colors"
                        >
                            Top Coupons
                        </Link>

                        {/* Dynamic Categories */}
                        {categories.map((category) => (
                            <div key={category.id} className="flex flex-col gap-3">
                                <Link
                                    href={categoryPagePath(category.slug)}
                                    onClick={() => setIsOpen(false)}
                                    className="text-2xl font-bold text-white hover:text-[#00ff9d] transition-colors"
                                >
                                    {category.name}
                                </Link>

                                {/* Subcategories */}
                                {category.children && category.children.length > 0 && (
                                    <div className="flex flex-col gap-2 pl-4 border-l border-white/10 ml-[45%]">
                                        {category.children.map((child: any) => (
                                            <Link
                                                key={child.id}
                                                href={`/${child.slug}/coupons`}
                                                onClick={() => setIsOpen(false)}
                                                className="text-lg text-gray-400 hover:text-white transition-colors text-left"
                                            >
                                                {child.name}
                                            </Link>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}

                        {/* All Stores (Last) */}
                        <Link
                            href="/stores"
                            onClick={() => setIsOpen(false)}
                            className="text-2xl font-bold text-white hover:text-[#00ff9d] transition-colors"
                        >
                            All Stores
                        </Link>

                        {/* Custom Links from Settings */}
                        {links.map((link, i) => (
                            <Link
                                key={i}
                                href={link.href}
                                onClick={() => setIsOpen(false)}
                                className="text-2xl font-bold text-white hover:text-[#00ff9d] transition-colors"
                            >
                                {link.label}
                            </Link>
                        ))}

                        <div className="h-px bg-white/10 my-4 w-full" />

                        <Link
                            href="/submit-coupon"
                            onClick={() => setIsOpen(false)}
                            className="text-xl font-bold text-[#00ff9d] hover:text-[#00cc7d] transition-colors flex items-center justify-center gap-2"
                        >
                            Submit a Coupon
                        </Link>

                        {!user && (
                            <Link
                                href="/login"
                                onClick={() => setIsOpen(false)}
                                className="text-xl font-bold text-white hover:text-[#00ff9d] transition-colors mt-4"
                            >
                                Sign In / Join
                            </Link>
                        )}
                    </nav>
                </div>
            )}
        </div>
    );
}
